import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { getUserDateFormat } from '@/common/utils/utils';
import type { DeviceLine } from '../../SensorTimeline/lib/chartModel';
import type { Device } from '../../SensorTimeline/lib/devices';
import type { Extent } from '../../SensorTimeline/lib/extent';
import { formatReading, valueAt } from '../../SensorTimeline/lib/hover';
import { formatTooltipTime } from '../../SensorTimeline/lib/time';
import { useBesideCursor } from '../../SensorTimeline/useBesideCursor';
import type { Hover } from '../../SensorTimeline/useCrosshair';

interface GxpChartTooltipProps {
  hover: Hover;
  lines: readonly DeviceLine[];
  devices: readonly Device[];
  sensorLimits: ReadonlyMap<string, Extent>;
}

/**
 * The hovered reading's UTC time, then each sensor's value and whether it is in or out of its own limits (PRD 4.1);
 * no verdict when a sensor has no limits. Fixed to the viewport beside the cursor, as the vitals tooltip is.
 */
const GxpChartTooltip = ({
  hover,
  lines,
  devices,
  sensorLimits,
}: GxpChartTooltipProps) => {
  const ref = useRef<HTMLDivElement>(null);
  useBesideCursor(ref, hover.clientX, hover.clientY);

  const rows = devices.flatMap((device) => {
    const line = lines.find((candidate) => candidate.device.id === device.id);
    const value = line ? valueAt(line, hover.at, hover.tolerance) : null;
    return value === null ? [] : [{ device, value }];
  });

  return createPortal(
    <div ref={ref} role='tooltip' className='gxp-chart-tooltip'>
      <div className='gxp-chart-tooltip__time'>
        {`${formatTooltipTime(hover.at, 'UTC', getUserDateFormat())} UTC`}
      </div>
      {rows.length === 0 && (
        <div className='gxp-chart-tooltip__missing'>No data</div>
      )}
      {rows.map(({ device, value }) => {
        const limits = sensorLimits.get(device.id);
        const inRange =
          limits !== undefined && value >= limits[0] && value <= limits[1];
        return (
          <div key={device.id} className='gxp-chart-tooltip__row'>
            <span className='gxp-chart-tooltip__device'>{device.label}</span>
            <span className='gxp-chart-tooltip__value'>
              {formatReading(value, '°C')}
            </span>
            {limits && (
              <span
                className={
                  inRange
                    ? 'gxp-chart-tooltip__status'
                    : 'gxp-chart-tooltip__status gxp-chart-tooltip__status--out'
                }
              >
                {inRange ? 'In range' : 'Out of range'}
              </span>
            )}
          </div>
        );
      })}
    </div>,
    document.body
  );
};

export default GxpChartTooltip;
